import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { CheckIcon, CrossIcon } from '../../components/ui/icons';
import {
  STEPS,
  engineering,
  fromPosition,
  parseEngineering,
  toPosition,
  unitOf,
} from './rangeScale';

/** What one column is filtering by. A numeric column uses the bounds, a text
 *  one uses `text`; both are absent when the column is not filtering. */
export interface ColumnFilterValue {
  text?: string;
  min?: string;
  max?: string;
}

export function isFiltering(value: ColumnFilterValue | undefined): boolean {
  if (!value) return false;
  return Boolean(value.text?.trim() || value.min?.trim() || value.max?.trim());
}

interface Props {
  title: string;
  numeric: boolean;
  /** Where to put it: the rectangle of the heading that was pressed. */
  box: DOMRect;
  value: ColumnFilterValue | undefined;
  /** The smallest and largest this column actually holds right now, with every
   *  other column's filter already applied, already written the way the column
   *  writes them — "4.7k", not 4700. Null when the column is not numeric, or
   *  when nothing is left to measure. */
  bounds: { min: string; max: string } | null;
  /** Said under the fields when the range is not a plain number: a resistance
   *  is typed as "4k7", and the person has to be told that is allowed. */
  hint?: string;
  onApply: (value: ColumnFilterValue) => void;
  onClear: () => void;
  onClose: () => void;
}

/**
 * One column's filter, as a small form over the table.
 *
 * A box under every heading was the obvious thing and the wrong one: eleven
 * boxes across the top, each too narrow to read, and a range — "between 16 and
 * 50 volts" — cannot be written into one of them at all. So the heading carries
 * a funnel instead, and the form appears where it was pressed.
 *
 * For a numeric column the two fields open already holding the range the column
 * actually spans, so the first thing the form says is what there is: "6.3 to
 * 50" is an answer on its own, before anything is narrowed.
 *
 * Rendered into the body rather than into the cell. The table sits in a box that
 * scrolls sideways, and a scroll box clips in both directions — the form would
 * be cut off by the very element it belongs to.
 */
export function ColumnFilter({
  title,
  numeric,
  box,
  value,
  bounds,
  hint,
  onApply,
  onClear,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const card = useRef<HTMLDivElement>(null);

  const [text, setText] = useState(value?.text ?? '');
  const [min, setMin] = useState(value?.min ?? bounds?.min ?? '');
  const [max, setMax] = useState(value?.max ?? bounds?.max ?? '');

  /** The numeric span the handles travel, and how to write a value back into
   *  the fields. Absent when the column has no span to speak of — one row, or
   *  every row the same — and then there is no slider at all. */
  const span = useMemo(() => {
    if (!numeric || !bounds) return null;
    const low = parseEngineering(bounds.min);
    const high = parseEngineering(bounds.max);
    if (low === null || high === null || !(high > low)) return null;
    return { low, high, unit: unitOf(bounds.max) };
  }, [numeric, bounds]);

  /** Written the way the column writes itself, so that what the slider puts in
   *  the field is the same shape as what was already there. */
  const write = (amount: number) => engineering(amount, span?.unit ?? '');

  // The fields are the truth and the handles follow them, not the other way
  // round: somebody typing "4k7" must not have it rounded to the nearest stop
  // under their fingers.
  const lowPos = span ? toPosition(parseEngineering(min) ?? span.low, span.low, span.high) : 0;
  const highPos = span ? toPosition(parseEngineering(max) ?? span.high, span.low, span.high) : STEPS;

  // Escape and a press outside both mean "never mind". Capture phase, or a
  // press that lands on a row would open that part before this closed.
  useEffect(() => {
    const down = new Set<number>();

    const away = (event: PointerEvent) => {
      down.add(event.pointerId);
      // Not a press at all: three fingers landing at once is how a phone is
      // told to take a screenshot, and it used to close this form before the
      // picture was taken — so the one thing nobody could photograph was this.
      if (down.size > 1) return;
      if (!card.current?.contains(event.target as Node)) onClose();
    };
    const up = (event: PointerEvent) => down.delete(event.pointerId);
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', away, true);
    document.addEventListener('pointerup', up, true);
    document.addEventListener('pointercancel', up, true);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away, true);
      document.removeEventListener('pointerup', up, true);
      document.removeEventListener('pointercancel', up, true);
      document.removeEventListener('keydown', key);
    };
  }, [onClose]);

  const width = 232;
  const left = Math.min(Math.max(8, box.left), window.innerWidth - width - 8);

  return createPortal(
    <div
      ref={card}
      className="column-filter"
      style={{ position: 'fixed', left, top: box.bottom + 4, width }}
      role="dialog"
      aria-label={title}
    >
      {/* The name of the column and the two answers, on one line at the top.
          They used to sit at the bottom, under a four-line note about writing
          "4k7" — and on a phone that note pushed them off the screen, so the
          form could be filled in and not applied. */}
      <div className="column-filter__head">
        <strong className="column-filter__title">{title}</strong>
        <div className="column-filter__actions">
          <button
            type="button"
            className="btn column-filter__icon"
            onClick={onClear}
            title={t('stock.table.clear')}
            aria-label={t('stock.table.clear')}
          >
            <CrossIcon />
          </button>
          <button
            type="button"
            className="btn btn--primary column-filter__icon"
            onClick={() => onApply(numeric ? { min, max } : { text })}
            title={t('stock.table.apply')}
            aria-label={t('stock.table.apply')}
          >
            <CheckIcon />
          </button>
        </div>
      </div>

      {numeric ? (
        <>
          {/* The slider first: it is the answer to "somewhere in the middle",
              which is most of what a range filter is asked for. The fields
              below stay because "exactly 16 to 50 volts" is the rest of it, and
              no handle is aimed that precisely. */}
          {span && (
            <div className="range-slider">
              <div className="range-slider__track">
                <div
                  className="range-slider__span"
                  style={{
                    left: `${(lowPos / STEPS) * 100}%`,
                    right: `${100 - (highPos / STEPS) * 100}%`,
                  }}
                />
              </div>
              <input
                type="range"
                min={0}
                max={STEPS}
                value={lowPos}
                aria-label={t('stock.table.from')}
                onChange={(event) => {
                  const position = Math.min(Number(event.target.value), highPos);
                  setMin(write(fromPosition(position, span.low, span.high)));
                }}
              />
              <input
                type="range"
                min={0}
                max={STEPS}
                value={highPos}
                aria-label={t('stock.table.to')}
                onChange={(event) => {
                  const position = Math.max(Number(event.target.value), lowPos);
                  setMax(write(fromPosition(position, span.low, span.high)));
                }}
              />
            </div>
          )}

          <div className="column-filter__range">
            <label>
              <span>{t('stock.table.from')}</span>
              <input
                value={min}
                onChange={(event) => setMin(event.target.value)}
                inputMode="decimal"
                autoFocus
              />
            </label>
            <label>
              <span>{t('stock.table.to')}</span>
              <input
                value={max}
                onChange={(event) => setMax(event.target.value)}
                inputMode="decimal"
              />
            </label>
          </div>
          {bounds && (
            <p className="hint">
              {t('stock.table.range', { min: bounds.min, max: bounds.max })}
            </p>
          )}
          {hint && <p className="hint">{hint}</p>}
        </>
      ) : (
        <input
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder={t('stock.table.filter')}
          autoFocus
        />
      )}

    </div>,
    document.body,
  );
}
